import React, { useRef, useState, useMemo, useEffect } from 'react';
import AriaModal from 'react-aria-modal';
import { SkeletonLoader } from './'
import { createPortal } from 'react-dom'
import { useModalDialog } from '../hooks'

const withModalContent = (WrappedComponent, props) => {

  // Use a HOC to add loading state.
  // Putting this in the Context or ModalDialog component causes the modal "body" component to lose state when the loading state changes

  return ({ loadingModalDialog }) => {
    const [loading, setLoading] = useState(false)

    const bodyRef = useRef()

    let {title, contentProps, submitLabel, submitLabelLoading, cancelLabel, onSubmit, onCancel, customButtons} = props || {}

    return (
      <div className="ModalDialog" tabIndex={loadingModalDialog ? "0" : "-1"}>
        <div className="modal-container">

          <div className="modal-container-header">
            {title}
          </div>

          <div className="modal-container-body" ref={bodyRef}>
            {
              (WrappedComponent && <WrappedComponent {...contentProps} loading={loading} setLoading={setLoading} />) || <React.Fragment><SkeletonLoader /><ul><SkeletonLoader width={150}/></ul></React.Fragment>
            }
          </div>

          <div className="modal-container-footer">
            { customButtons &&

              customButtons.map( (button, index) => {
                return (
                  <button 
                    tabIndex={0} 
                    key={index}
                    onClick={ () => button.onClick(bodyRef) }
                    className="footer-item button"
                    disabled={loadingModalDialog || loading}
                    title={button.tooltip}
                  >
                    <span>{button.label}</span>
                  </button>
                )
              })

            }
            { submitLabel &&
              <button 
                tabIndex={0} 
                onClick={ onSubmit } 
                className={"footer-item button main-button" + (loading ? ' loading' : '')}
                disabled={loadingModalDialog || loading}
              >
                <span>{loading ? submitLabelLoading : submitLabel}</span>
              </button>
            }
            { cancelLabel &&
              <button 
                tabIndex={0} 
                onClick={ onCancel } 
                className="footer-item button"
                disabled={loadingModalDialog || loading}
              >
                <span>{cancelLabel}</span>
              </button>
            }
          </div>

        </div>
      </div>
    )
  }
}

export const ModalDialog = () => {

  const { show, modalDialog, modalStack, loadingModalDialog } = useModalDialog()

  const getApplicationNode = () => {
    return document.getElementById('root');
  };

  // Stable identity across renders — recreating this (e.g. calling renderTo fresh
  // every render) would remount the whole modal subtree, including the layers below,
  // whenever loadingModalDialog toggles.
  const Modal = useMemo(() => AriaModal.renderTo('#modal'), [])

  // showModalDialog can "stack" a dialog on top of another (e.g. "Nytt
  // företag" opened from inside "Ny person") — modalStack holds the paused
  // ones underneath the current modalDialog. All of them render here as
  // permanent siblings (hidden via CSS, not conditionally unmounted) so a
  // paused form's state (whatever the user already typed) survives while a
  // dialog on top of it is open, and reappears untouched once that one
  // closes and hideModalDialog pops back to it. Swapping which one is
  // "current" would unmount/remount the paused one and wipe its state.
  const layers = show ? [...modalStack, modalDialog].filter(l => l && l.Content) : []
  const topLayer = layers[layers.length - 1]

  const componentCache = useRef(new Map())
  const getLayerComponent = (layer) => {
    if (!componentCache.current.has(layer.id)) {
      componentCache.current.set(layer.id, withModalContent(layer.Content, layer))
    }
    return componentCache.current.get(layer.id)
  }

  // Drop cache entries for dialogs that have been closed for good (not just
  // paused), so it doesn't grow unbounded over a long session.
  useEffect(() => {
    const alive = new Set(layers.map(l => l.id))
    for (const key of componentCache.current.keys()) {
      if (!alive.has(key)) componentCache.current.delete(key)
    }
  })

  return show && topLayer &&

    <Modal
      titleText={topLayer.title}
      verticallyCenter={false}
      focusDialog={true}
      getApplicationNode={getApplicationNode}
      underlayClass={'modal-underlay'}
    >

      { layers.map((layer, i) => {
        const LayerComponent = getLayerComponent(layer)
        const isTop = i === layers.length - 1
        return (
          <div key={layer.id} style={ isTop ? undefined : { display: 'none' } }>
            <LayerComponent loadingModalDialog={ isTop ? loadingModalDialog : false } />
          </div>
        )
      }) }

    </Modal>

};
